import {
  type ActionRecord,
  type ColumnKey,
  type SectionRecord
} from "../types.ts"
import type { PermissionLabel } from "../types/permissions.types.ts"

export const getTotalCounts = (subKeys: SectionRecord) => {
  const allActions = Object.values(subKeys).flatMap((actions) =>
    Object.values(actions)
  )
  return {
    enabled: allActions.filter(Boolean).length,
    total: allActions.length
  }
}
const COLUMN_KEYS: ColumnKey[] = [
  "full_access",
  "create",
  "view",
  "delete",
  "edit"
]

export const getColKey = (action: string): ColumnKey | null => {
  for (const key of COLUMN_KEYS) {
    if (action === key || action.endsWith(`_${key}`)) return key
  }
  return null
}

export const getActionExtras = (actions: ActionRecord) =>
  Object.entries(actions).filter(([action]) => getColKey(action) === null)

export const getFullAccessKey = (actions: ActionRecord): string | undefined =>
  Object.keys(actions).find((key) => getColKey(key) === "full_access")

/**
 * Synchronizes full_access with individual permissions (create, view, delete, edit)
 * @param actions - Current actions object for a specific permission
 * @param changedKey - The key that was changed
 * @param newValue - The new value for the changed key
 * @returns Updated actions object with synchronized full_access
 */
export const syncFullAccess = (
  actions: ActionRecord,
  changedKey: string,
  newValue: boolean
): ActionRecord => {
  const changedColKey = getColKey(changedKey)
  const updatedActions = { ...actions, [changedKey]: newValue }

  // Find keys for each permission type
  const fullAccessKey = Object.keys(actions).find(
    (key) => getColKey(key) === "full_access"
  )
  const createKey = Object.keys(actions).find(
    (key) => getColKey(key) === "create"
  )
  const viewKey = Object.keys(actions).find((key) => getColKey(key) === "view")
  const deleteKey = Object.keys(actions).find(
    (key) => getColKey(key) === "delete"
  )
  const editKey = Object.keys(actions).find((key) => getColKey(key) === "edit")

  // If full_access is being turned ON, enable all other permissions
  if (changedColKey === "full_access" && newValue) {
    if (createKey) updatedActions[createKey] = true
    if (viewKey) updatedActions[viewKey] = true
    if (deleteKey) updatedActions[deleteKey] = true
    if (editKey) updatedActions[editKey] = true
  }
  // If full_access is being turned OFF, disable all other permissions
  else if (changedColKey === "full_access" && !newValue) {
    if (createKey) updatedActions[createKey] = false
    if (viewKey) updatedActions[viewKey] = false
    if (deleteKey) updatedActions[deleteKey] = false
    if (editKey) updatedActions[editKey] = false
  }
  // If any individual permission is being changed, update full_access accordingly
  else if (changedColKey !== "full_access" && fullAccessKey) {
    // Check if all individual permissions are enabled
    const allEnabled =
      (!createKey || updatedActions[createKey] === true) &&
      (!viewKey || updatedActions[viewKey] === true) &&
      (!deleteKey || updatedActions[deleteKey] === true) &&
      (!editKey || updatedActions[editKey] === true)

    updatedActions[fullAccessKey] = allEnabled
  }

  return updatedActions
}

// PermissionLabel.name is max_length=100 on the model, and the duplicate flow
// bypasses the Add/Rename modal's own 50-char rule.
const MAX_LABEL_NAME_LENGTH = 100

export const getDuplicateName = (
  name: string,
  labels: PermissionLabel[]
): string => {
  const taken = new Set(
    labels.map((l) => l?.name?.toLowerCase()).filter(Boolean)
  )

  for (let attempt = 1; ; attempt++) {
    const suffix = attempt === 1 ? " (Copy)" : ` (Copy ${attempt})`
    const base = name.slice(0, MAX_LABEL_NAME_LENGTH - suffix.length)
    const candidate = `${base}${suffix}`

    if (!taken.has(candidate.toLowerCase())) return candidate
  }
}

export const getDuplicateCode = (
  code: string,
  labels: PermissionLabel[]
): string => {
  const baseCode = code.replace(/_\d+$/, "")

  const matchingCodes = labels
    .map((l) => l.code)
    .filter((c) => c === baseCode || new RegExp(`^${baseCode}_\\d+$`).test(c))

  const maxNumber = matchingCodes.reduce((max, current) => {
    const match = current.match(/_(\d+)$/)

    if (!match) return Math.max(max, 0)

    return Math.max(max, Number(match[1]))
  }, 0)

  return `${baseCode}_${maxNumber + 1}`
}
